import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SaviBusinessJsonLd from "../../components/SaviBusinessJsonLd";
import SaviQuiz from "../../components/SaviQuiz";
import { ArrowRight, MapPin, Wifi, Users, Mountain, Dumbbell, Coffee, Sun, Moon } from "lucide-react";
import { pic } from "../../lib/pic";

const ColivingValenciaPage = () => {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Coliving Valencia | Savi — A Quieter Way to Live & Work</title>
        <meta name="description" content="Coliving in Valencia, Spain — but not in the city. Savi is a private villa in the mountains for founders and creatives. Sober, focused, intentional. From €1,200/month." />
        <link rel="canonical" href="https://www.savicoliving.com/coliving-valencia" />
        <meta property="og:title" content="Coliving Valencia | Savi Coliving" />
        <meta property="og:description" content="Not another city apartment with a shared kitchen. A villa in the mountains, 25 min from Valencia. For people doing real work." />
        <meta property="og:image" content="https://static.wixstatic.com/media/447ef7_c4f040f251774723aaf22ea2f5874e83~mv2.jpg" />
      </Helmet>
      <SaviBusinessJsonLd id="coliving-valencia" />

      {/* Minimal header — not the main nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FBF7F1]/95 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <Link to="/" className="text-sm text-[#C85A38] font-medium hover:underline">View the house →</Link>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-14">
        <div className="relative h-[75vh] overflow-hidden">
          <img {...pic("villa-front")} fetchpriority="high" decoding="async" alt="Savi Coliving villa in the mountains near Valencia" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2E2A26] via-[#2E2A26]/50 to-[#2E2A26]/30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-4 max-w-4xl leading-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.5)'}}>
              Coliving in Valencia — just not<br className="hidden sm:block" /> where you'd expect.
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl mb-8" style={{textShadow: '0 2px 8px rgba(0,0,0,0.4)'}}>
              25 minutes from the city. A private villa in the mountains. For founders and creatives who want to actually get things done.
            </p>
            <Link to="/">
              <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-10 py-4 text-sm font-medium tracking-wide transition-all">
                View the house <ArrowRight className="inline ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* STEVE INTRO */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-[#2E2A26]/80 text-base leading-relaxed mb-4">
            I'm Steve. I built Savi because I spent years looking for a coliving in Valencia that wasn't just a shared apartment with good branding.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            Most <strong className="text-[#2E2A26]/80">coliving spaces in Valencia</strong> are city apartments. Thin walls, street noise, rotating cast of people staying a week or two. Fine if you're passing through. Not great if you're trying to build something.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            So I found a villa in the Sierra Calderona mountains — 25 minutes from Valencia — and turned it into something different. Not a hostel, not a retreat, not a corporate "co-living experience." Just a house where a small group of focused people live and work together for a few months at a time.
          </p>
        </div>
      </section>

      {/* REALITY CHECK */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">What "coliving Valencia" usually means</h2>
          <div className="space-y-3 mb-8">
            {[
              "A converted apartment in Ruzafa or El Carmen",
              "Shared with 8-15 people you've never met",
              "Someone's always on a different schedule — or a different planet",
              "\"Community events\" that are really just pub crawls",
              "People leave after 2 weeks before you learn their last name"
            ].map((item, i) => (
              <p key={i} className="text-[#2E2A26]/50 text-sm pl-4 border-l-2 border-stone-200">{item}</p>
            ))}
          </div>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            That's not bad. It's just not what everyone needs. If you're here searching for <strong className="text-[#2E2A26]/80">coliving in Valencia, Spain</strong>, you probably already know that.
          </p>
        </div>
      </section>

      {/* WHAT THIS IS */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">What Savi actually is</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {[
              { icon: Mountain, text: "A villa in the mountains, 25 minutes from Valencia" },
              { icon: Users, text: "up to 16 people, staying 1-3 months each" },
              { icon: Wifi, text: "Starlink WiFi up to 500Mbps, coworking with screens" },
              { icon: Dumbbell, text: "Pool, gym, roof terrace, hiking from the door" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#F4ECE2] rounded-xl">
                <item.icon className="w-5 h-5 text-[#C85A38] mt-0.5 flex-shrink-0" />
                <p className="text-[#2E2A26]/70 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed">
            No alcohol. No party culture. Everyone's here because they're working on something and they want to be around others doing the same. It's a <strong className="text-[#2E2A26]/80">quiet coliving near Valencia</strong> that actually delivers on the promise.
          </p>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-12 px-4 bg-[#2E2A26]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-6">Who comes here</h2>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            Founders. Freelancers. Developers. Designers. Writers. People building startups, creative projects, or just trying to figure out what's next — without the noise.
          </p>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            Most guests are <strong className="text-white/80">digital nomads</strong> or remote workers from across Europe and beyond. They've tried the Bali thing, the Lisbon thing, maybe even a <strong className="text-white/80">digital nomad coliving in Valencia</strong> before. They come here because they want depth, not just another co-working desk with a view.
          </p>
          <p className="text-white/40 text-sm">
            We review every application. Not everyone's the right fit — and that's the point.
          </p>
        </div>
      </section>

      {/* DAILY LIFE */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">A typical day</h2>
          <div className="space-y-4">
            {[
              { icon: Sun, time: "7-9am", text: "Coffee on the terrace. Sunrise over the mountains. Most people are up early — not because of rules, but because there's no reason not to be." },
              { icon: Coffee, time: "9am-1pm", text: "Deep work in the coworking space. Dual screens, standing desks, fast WiFi. Skill shares some mornings. Accountability sessions weekly." },
              { icon: Dumbbell, time: "Afternoon", text: "Gym, pool, a hike in the sierra. Some people drive into Valencia for the afternoon. Space to move, think, or do nothing." },
              { icon: Moon, time: "Evening", text: "Someone cooks. Everyone eats together on the roof terrace. Conversations that actually go somewhere. No alcohol. Bed by 11." }
            ].map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#C85A38]/10 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#C85A38]" />
                </div>
                <div>
                  <p className="text-[#C85A38] text-xs font-medium mb-1">{item.time}</p>
                  <p className="text-[#2E2A26]/60 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">The location question</h2>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            "But it's not in Valencia city?" — that's the first thing people ask. And yes, we're 25 minutes away in the Sierra Calderona mountains. A few guests drive in regularly. Most find they don't want to.
          </p>
          <p className="text-[#2E2A26]/60 text-sm leading-relaxed mb-4">
            The distance is the feature, not the bug. You came here to focus, not to be five minutes from the beach bars. Valencia is there when you need it. The mountains are here when you need to work.
          </p>
          <div className="flex items-start gap-2 p-4 bg-white rounded-xl border border-stone-100">
            <MapPin className="w-4 h-4 text-[#C85A38] mt-0.5 flex-shrink-0" />
            <p className="text-[#2E2A26]/60 text-sm">Náquera, Sierra Calderona — 25 min from Valencia city, 35 min from the airport, 15 min from the beach at Puçol.</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-6">What people say</h2>
          <div className="space-y-4">
            {[
              { quote: "A rare environment where you can slow down, connect, and leave clearer, more focused, and recharged than when you arrived. When I'm back in Spain, this is where I'll come home to.", name: "Travis Weeks", detail: "Resident, 90-day stay", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/tc416uth_travis.jpg" },
              { quote: "I've come across many co-working and co-living spaces through my work — Savi, with its sober concept, is truly something very special. A place that leaves a lasting impression.", name: "Saskia Rehhahn", detail: "Hospitality professional", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/wr0ogbx9_saskia.jpeg" },
              { quote: "I genuinely couldn't have found a better co-living spot in Valencia. The location is incredible. The community is warm. The standard of hosting is on another level.", name: "Devinn", detail: "Resident, Google Local Guide", img: "https://customer-assets.emergentagent.com/job_5ed0a8fe-7bde-4cf0-886b-3137a44819c8/artifacts/ovbdty6h_devvo.jpg" }
            ].map((t, i) => (
              <blockquote key={i} className="bg-[#F4ECE2] rounded-xl p-5">
                <p className="text-[#2E2A26]/70 text-sm leading-relaxed mb-3">"{t.quote}"</p>
                <div className="flex items-center gap-2.5">
                  <img src={t.img} alt={t.name} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
                  <p className="text-[#2E2A26]/90 text-xs font-medium">{t.name} <span className="text-[#2E2A26]/40">— {t.detail}</span></p>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* QUIZ */}
      <section className="py-12 px-4 bg-[#F4ECE2]">
        <div className="max-w-xl mx-auto text-center mb-8">
          <p className="text-[#C85A38] text-xs font-medium tracking-[0.2em] uppercase mb-2">30 seconds</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-2">Is Savi right for you?</h2>
          <p className="text-[#2E2A26]/50 text-sm">Four quick questions. No sign-up required.</p>
        </div>
        <SaviQuiz />
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-[#2E2A26]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Interested?</h2>
          <p className="text-white/50 text-sm mb-8 max-w-md mx-auto">
            We only take up to 16 guests at a time. Most months fill up. If this sounds like what you need, take a look at the house and apply.
          </p>
          <Link to="/">
            <button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-12 py-5 text-sm font-medium transition-all">
              View Savi Coliving <ArrowRight className="inline ml-2 w-4 h-4" />
            </button>
          </Link>
          <p className="text-white/30 text-xs mt-6">Application only · Each stay is personally reviewed</p>
        </div>
      </section>

      {/* RELATED PAGES */}
      <section className="py-10 px-4 bg-[#FBF7F1]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#2E2A26]/30 text-xs uppercase tracking-wider mb-4">Related</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/quiet-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Quiet Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/luxury-coliving-valencia" className="text-[#C85A38] text-xs hover:underline">Luxury Coliving Valencia</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/coliving-valencia-for-founders" className="text-[#C85A38] text-xs hover:underline">Coliving for Founders</Link>
            <span className="text-[#2E2A26]/20">·</span>
            <Link to="/rooms" className="text-[#C85A38] text-xs hover:underline">Rooms & Pricing</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-6 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#2E2A26]/40 text-xs">&copy; 2026 SAVI Coliving</p>
          <div className="flex gap-4 text-xs text-[#2E2A26]/40">
            <Link to="/privacy" className="hover:text-[#2E2A26]">Privacy</Link>
            <Link to="/cookies" className="hover:text-[#2E2A26]">Cookies</Link>
            <Link to="/" className="hover:text-[#2E2A26]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ColivingValenciaPage;
